import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { filter, Subscription } from 'rxjs';

/**
 * BesharpBreadcrumbsComponent
 *
 * This component generates a dynamic breadcrumb navigation bar based on the current Angular route.
 * It supports custom home paths, optional hiding of route titles, and integration with PrimeNG's BreadcrumbModule.
 */
@Component({
  selector: 'app-besharp-breadcrumbs',
  imports: [BreadcrumbModule],
  templateUrl: './besharp-breadcrumbs.component.html',
  styleUrl: './besharp-breadcrumbs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BesharpBreadcrumbsComponent implements OnInit, OnDestroy {
  /**
   * The route path for the home breadcrumb. Defaults to '/app'.
   */
  @Input()
  homePath: string = '/app';

  /**
   * If true, disables the use of route titles in breadcrumbs. Defaults to false.
   */
  @Input()
  forceNoTitle?: boolean;

  /**
   * The array of breadcrumb items to display (PrimeNG MenuItem[]).
   */
  protected items?: MenuItem[];

  /**
   * The home breadcrumb item (PrimeNG MenuItem).
   */
  protected home?: MenuItem;

  /**
   * Subscription to router events for updating breadcrumbs on navigation.
   */
  private routerSub?: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdRef: ChangeDetectorRef
  ) {
    this.forceNoTitle = false;
  }

  /**
   * Angular lifecycle hook: Initializes the component, subscribes to router events, and builds the breadcrumbs.
   */
  ngOnInit(): void {
    this.refreshBreadcrumbs();
    this.routerSub = this.router.events
      .pipe(filter((event: any) => event instanceof NavigationEnd))
      .subscribe((_event: NavigationEnd) => {
        this.refreshBreadcrumbs();
      });
  }

  /**
   * Angular lifecycle hook: Cleans up the router event subscription on component destroy.
   */
  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  /**
   * Rebuilds the breadcrumb trail based on the current route and updates the home item.
   */
  private refreshBreadcrumbs(): void {
    this.items = [...this.createBreadcrumbs(this.route.root)];
    this.home = { icon: 'pi pi-home', command: () => this.router.navigate([this.homePath]) };
    this.cdRef.detectChanges();
  }
  /**
   * Recursively traverses the route tree to build the breadcrumb array.
   * @param route The current ActivatedRoute node.
   * @param url The accumulated URL string (default '').
   * @param breadcrumbs The array of MenuItem breadcrumbs (default []).
   * @returns MenuItem[] The constructed breadcrumb array.
   */
  private createBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: MenuItem[] = []): MenuItem[] {
    const children: ActivatedRoute[] = route.children;

    for (const child of children) {
      const routeURL: string = child.snapshot.url.map((segment) => segment.path).join('/');
      if (routeURL) {
        url += `/${routeURL}`;
      }

      const label = child.snapshot.title && !this.forceNoTitle ? child.snapshot.title : child.snapshot.url.toString();
      if (label) {
        const isLast = child.children.length === 0;
        const queryParams = isLast ? child.snapshot.queryParams : {};
        const queryString = this.buildQueryString(queryParams);

        const finalUrl = queryString ? `${url}${queryString}` : url;
        breadcrumbs.push({ label, url: finalUrl, command: () => this.router.navigate([finalUrl]) });
      }

      return this.createBreadcrumbs(child, url, breadcrumbs); // recurse down the tree
    }

    return breadcrumbs;
  }

  /**
   * Converts query parameters into a query string for breadcrumb links.
   * @param params The query parameters object.
   * @returns string The query string (e.g., '?key=value').
   */
  private buildQueryString(params: { [key: string]: any }): string {
    const keys = Object.keys(params);
    if (keys.length === 0) return '';
    const query = keys.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`).join('&');
    return `?${query}`;
  }
}
